import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getLeoTimestamp,
  LeoTimestamp,
  getBoolean,
  getOptionalLeoTimestamp,
  getList,
} from "@surya-digital/leo-ts-types";
import { PublicAPIKeyStatus } from "./publicApiKeyStatus";

export interface PublicAPIKeyDetailsDTO {
  apiKeyId: string;
  status: PublicAPIKeyStatus.PublicAPIKeyStatusDTO;
  expiresAt: string;
  isAdmin: boolean;
  createdAt: string;
  modifiedAt?: string | null;
  projectIds: string[];
}

export class PublicAPIKeyDetails {
  readonly isPublicAPIKeyDetailsPublicAPIKeyDetails: boolean = true;

  readonly apiKeyId: LeoUUID;
  readonly status: PublicAPIKeyStatus.PublicAPIKeyStatus;
  readonly expiresAt: LeoTimestamp;
  readonly isAdmin: boolean;
  readonly createdAt: LeoTimestamp;
  readonly modifiedAt: LeoTimestamp | null;
  readonly projectIds: LeoUUID[];


  constructor(
  apiKeyId: LeoUUID, 
  status: PublicAPIKeyStatus.PublicAPIKeyStatus, 
  expiresAt: LeoTimestamp, 
  isAdmin: boolean, 
  createdAt: LeoTimestamp, 
  modifiedAt: LeoTimestamp | null = null, 
  projectIds: LeoUUID[]
  ) {
    this.apiKeyId = apiKeyId;
    this.status = status;
    this.expiresAt = expiresAt;
    this.isAdmin = isAdmin;
    this.createdAt = createdAt;
    this.modifiedAt = modifiedAt;
    this.projectIds = projectIds;
  }

  static fromDTO(dto: PublicAPIKeyDetailsDTO): PublicAPIKeyDetails {
    const apiKeyId = getLeoUuid(dto, "apiKeyId", InvalidPublicAPIKeyDetailsApiKeyIdError);
    const status = PublicAPIKeyStatus.fromDTO(getObject(dto, "status", InvalidPublicAPIKeyDetailsStatusError));
    const expiresAt = getLeoTimestamp(dto, "expiresAt", InvalidPublicAPIKeyDetailsExpiresAtError);
    const isAdmin = getBoolean(dto, "isAdmin", InvalidPublicAPIKeyDetailsIsAdminError);
    const createdAt = getLeoTimestamp(dto, "createdAt", InvalidPublicAPIKeyDetailsCreatedAtError);
    const modifiedAt = getOptionalLeoTimestamp(dto, "modifiedAt", InvalidPublicAPIKeyDetailsModifiedAtError);
    const projectIds = getList<string, LeoUUID>(
      dto,
      "projectIds",
      (e: string) => new LeoUUID(e),
      InvalidPublicAPIKeyDetailsProjectIdsError
    );
    return new PublicAPIKeyDetails(
      apiKeyId,
      status,
      expiresAt,
      isAdmin,
      createdAt,
      modifiedAt,
      projectIds
    );
  }

  toDTO(): PublicAPIKeyDetailsDTO {
    const dto: PublicAPIKeyDetailsDTO = {
        apiKeyId: this.apiKeyId.toDTO(),
        status: PublicAPIKeyStatus.toDTO(this.status),
        expiresAt: this.expiresAt.toDTO(),
        isAdmin: this.isAdmin,
        createdAt: this.createdAt.toDTO(),
        projectIds: this.projectIds.map((e: LeoUUID) => { return e.toDTO(); })
    };
    if (this.modifiedAt !== undefined && this.modifiedAt !== null){
      dto.modifiedAt = this.modifiedAt.toDTO();
    }
    return dto;
  }
  }

PublicAPIKeyDetails.prototype.toString = function toString(): string {
  return `PublicAPIKeyDetails(apiKeyId='${this.apiKeyId}',status='${this.status}',expiresAt='${this.expiresAt}',isAdmin='${this.isAdmin}',createdAt='${this.createdAt}',modifiedAt='${this.modifiedAt}',projectIds='${this.projectIds}')`;
};

export class InvalidPublicAPIKeyDetailsError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPublicAPIKeyDetailsError";
  }
}
export class InvalidPublicAPIKeyDetailsApiKeyIdError extends InvalidPublicAPIKeyDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPublicAPIKeyDetailsApiKeyIdError";
  }
}

export class InvalidPublicAPIKeyDetailsStatusError extends InvalidPublicAPIKeyDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPublicAPIKeyDetailsStatusError";
  }
}

export class InvalidPublicAPIKeyDetailsExpiresAtError extends InvalidPublicAPIKeyDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPublicAPIKeyDetailsExpiresAtError";
  }
}

export class InvalidPublicAPIKeyDetailsIsAdminError extends InvalidPublicAPIKeyDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPublicAPIKeyDetailsIsAdminError";
  }
}

export class InvalidPublicAPIKeyDetailsCreatedAtError extends InvalidPublicAPIKeyDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPublicAPIKeyDetailsCreatedAtError";
  }
}

export class InvalidPublicAPIKeyDetailsModifiedAtError extends InvalidPublicAPIKeyDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPublicAPIKeyDetailsModifiedAtError";
  }
}

export class InvalidPublicAPIKeyDetailsProjectIdsError extends InvalidPublicAPIKeyDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPublicAPIKeyDetailsProjectIdsError";
  }
}
